import * as i0 from '@angular/core';
import { ElementRef, Component, Input, ViewChild, Directive, NgModule } from '@angular/core';
import { startWith, filter, Subject, firstValueFrom, takeUntil, BehaviorSubject } from 'rxjs';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@azavista/components/shared';
import { pick, AzavistaSharedModule } from '@azavista/components/shared';
import * as i1$1 from '@angular/material/tabs';

const EXPORTED_ELEMENT_WRAPPER_CLASS_NAME = 'aza-exported-element-wrapper';
const EXPORTED_ELEMENT_CONSUMER_CLASS_NAME = 'aza-exported-element-consumer';

class AzavistaExportedElementWrapperComponent {
    get templateRef() {
        this.onLoadRef('templateRef');
        return this._templateRef;
    }
    set elementRef(value) {
        if (value) {
            this._elementRef = value;
            this._elementRefLoaded$.next(value);
        }
    }
    get elementRef() {
        this.onLoadRef('elementRef');
        return this._elementRef;
    }
    get elementRefLoaded$() {
        return this._elementRefLoaded$.pipe(startWith(this._elementRef), filter(value => !!value?.nativeElement));
    }
    constructor(ngZone) {
        this.ngZone = ngZone;
        this.loadedElementRefType = null;
        this._elementRefLoaded$ = new Subject();
        this.isViewInit = false;
        this.className = EXPORTED_ELEMENT_WRAPPER_CLASS_NAME;
        this.onLoadRef = (refType) => {
            this.loadedElementRefType = refType;
            this.isViewInit = true;
        };
    }
    async ngAfterViewInit() {
        await firstValueFrom(this.ngZone.onStable);
        this.isViewInit = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaExportedElementWrapperComponent, deps: [{ token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.10", type: AzavistaExportedElementWrapperComponent, selector: "azavista-exported-element-wrapper", inputs: { loadedElementRefType: "loadedElementRefType" }, viewQueries: [{ propertyName: "_templateRef", first: true, predicate: ["exportedTemplateRef"], descendants: true }, { propertyName: "elementRef", first: true, predicate: ["exportedElementRef"], descendants: true, read: ElementRef }], ngImport: i0, template: "\r\n<ng-container *ngIf=\"isViewInit && loadedElementRefType !== 'templateRef'\">\r\n    {{ loadedElementRefType ?? 'inline' | apply : onLoadRef }}\r\n    <ng-container [ngTemplateOutlet]=\"exportedTemplateRef\"></ng-container>\r\n</ng-container>\r\n\r\n<ng-template #exportedTemplateRef>\r\n    <ng-container>\r\n        <div\r\n            #exportedElementRef\r\n            class=\"{{ className }} {{\r\n                className + '--' + loadedElementRefType\r\n            }}\"\r\n        >\r\n            <ng-container [ngTemplateOutlet]=\"ngContent\"></ng-container>\r\n        </div>\r\n    </ng-container>\r\n</ng-template>\r\n\r\n<ng-template #ngContent>\r\n    <ng-content *ngIf=\"isViewInit && loadedElementRefType\"></ng-content>\r\n</ng-template>\r\n", dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: i2.ApplyFunctionPipe, name: "apply" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaExportedElementWrapperComponent, decorators: [{
            type: Component,
            args: [{ selector: 'azavista-exported-element-wrapper', template: "\r\n<ng-container *ngIf=\"isViewInit && loadedElementRefType !== 'templateRef'\">\r\n    {{ loadedElementRefType ?? 'inline' | apply : onLoadRef }}\r\n    <ng-container [ngTemplateOutlet]=\"exportedTemplateRef\"></ng-container>\r\n</ng-container>\r\n\r\n<ng-template #exportedTemplateRef>\r\n    <ng-container>\r\n        <div\r\n            #exportedElementRef\r\n            class=\"{{ className }} {{\r\n                className + '--' + loadedElementRefType\r\n            }}\"\r\n        >\r\n            <ng-container [ngTemplateOutlet]=\"ngContent\"></ng-container>\r\n        </div>\r\n    </ng-container>\r\n</ng-template>\r\n\r\n<ng-template #ngContent>\r\n    <ng-content *ngIf=\"isViewInit && loadedElementRefType\"></ng-content>\r\n</ng-template>\r\n" }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }]; }, propDecorators: { loadedElementRefType: [{
                type: Input
            }], _templateRef: [{
                type: ViewChild,
                args: ['exportedTemplateRef']
            }], elementRef: [{
                type: ViewChild,
                args: ['exportedElementRef', { read: ElementRef }]
            }] } });

const STOP_PROPAGATION_KEYBOARD_KEYS = [
    'ArrowLeft',
    'ArrowDown',
    'ArrowUp',
    'ArrowRight',
];
const STOP_PROPAGATION_KEYBOARD_TAG_NAMES = [
    'INPUT',
    'TEXTAREA',
];
/**
 * Usage:
 ```
 <mat-tab-group matTabGroupUseExportedElement>
    <mat-tab
        [azavistaExportedElementInput]="dashboardElement?.exportedElement"
    >
        <azavista-dashboard #dashboardElement></azavista-dashboard>
    </mat-tab>
    <mat-tab [azavistaExportedElementInput]="wrapperElement">
        <azavista-exported-element-wrapper
            #wrapperElement
        ></azavista-exported-element-wrapper>
    </mat-tab>
    <mat-tab [azavistaExportedElementInput]="teamTable?.exportedElement">
        <azavista-table #teamTable></azavista-table>
    </mat-tab>
</mat-tab-group>
```
 */
class AzavistaMatTabGroupUseExportedElementDirective {
    constructor(element, host, ngZone) {
        this.element = element;
        this.host = host;
        this.ngZone = ngZone;
        this.onDestroy$ = new Subject();
    }
    ngAfterViewInit() {
        this.initiateAllTabsExportedElement();
        const tabHeader = this.getElementAsExportedElementContainer();
        if (!tabHeader) {
            return;
        }
        this.createExportedElementContainer(tabHeader);
        this.host.selectedTabChange.pipe(startWith({
            tab: this.host._allTabs.toArray()[this.host.selectedIndex ?? 0]
        }), takeUntil(this.onDestroy$)).subscribe(async (change) => {
            const selectedTab = change.tab;
            await this.loadExportedElementFromTab(selectedTab);
        });
    }
    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }
    async loadExportedElementFromTab(selectedTab) {
        const selectedTabExporter = selectedTab;
        if (!selectedTabExporter.exportedElement) {
            await firstValueFrom(this.ngZone.onStable);
        }
        this.loadExportedElement(selectedTabExporter?.exportedElement);
        selectedTabExporter?.exportedElementChanges$?.pipe(takeUntil(this.host.selectedIndexChange), takeUntil(this.onDestroy$)).subscribe((value) => {
            if (value) {
                this.loadExportedElement(value);
            }
        });
    }
    async loadExportedElement(exportedElement) {
        exportedElement?.onLoadRef('elementRef');
        if (this.exportedElementContainer) {
            this.exportedElementContainer.innerHTML = '';
            if (exportedElement) {
                const exportedElementElementRef = await firstValueFrom(exportedElement?.elementRefLoaded$);
                if (exportedElementElementRef) {
                    this.exportedElementContainer.appendChild(exportedElementElementRef.nativeElement);
                }
            }
        }
    }
    createExportedElementContainer(tabHeaderRef) {
        this.exportedElementContainer = document.createElement('div');
        this.exportedElementContainer.className = EXPORTED_ELEMENT_CONSUMER_CLASS_NAME;
        this.exportedElementContainer.addEventListener('keydown', event => {
            if (STOP_PROPAGATION_KEYBOARD_TAG_NAMES.includes(event.target?.tagName) || STOP_PROPAGATION_KEYBOARD_KEYS.includes(event.code)) {
                event.stopImmediatePropagation();
            }
        });
        tabHeaderRef.appendChild(this.exportedElementContainer);
    }
    getElementAsExportedElementContainer() {
        return this.element.nativeElement.querySelector('mat-tab-header .mat-mdc-tab-labels');
    }
    async initiateAllTabsExportedElement() {
        await firstValueFrom(this.ngZone.onStable);
        this.host._allTabs.forEach(tab => {
            const selectedTabExporter = tab;
            selectedTabExporter.exportedElement?.onLoadRef('elementRef');
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaMatTabGroupUseExportedElementDirective, deps: [{ token: i0.ElementRef }, { token: i1$1.MatTabGroup }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.10", type: AzavistaMatTabGroupUseExportedElementDirective, selector: "mat-tab-group[azavistaMatTabGroupUseExportedElement]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaMatTabGroupUseExportedElementDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'mat-tab-group[azavistaMatTabGroupUseExportedElement]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i1$1.MatTabGroup }, { type: i0.NgZone }]; } });

class AzavistaExportedElementBase {
    constructor() {
        this.exportedElementChanges$ = new BehaviorSubject(null);
    }
    get exportedElement() {
        return this.getExportedElement();
    }
    set exportedElement(value) {
        this.setExportedElement(value);
    }
    getExportedElement() {
        return this._exportedElement ?? null;
    }
    setExportedElement(value) {
        this._exportedElement = value;
        if (value) {
            this.exportedElementChanges$.next(value);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaExportedElementBase, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.10", type: AzavistaExportedElementBase, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaExportedElementBase, decorators: [{
            type: Directive
        }] });

class AzavistaExportedElementExporterDirective extends AzavistaExportedElementBase {
    get exportedElement() {
        return this.getExportedElement();
    }
    set exportedElement(value) {
        this.setExportedElement(value);
    }
    constructor(ngZone) {
        super();
        this.ngZone = ngZone;
    }
    async setExportedElement(value) {
        if (value) {
            await firstValueFrom(this.ngZone.onStable);
            super.setExportedElement(value);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaExportedElementExporterDirective, deps: [{ token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.10", type: AzavistaExportedElementExporterDirective, selector: "[azavistaExportedElementExporter]", viewQueries: [{ propertyName: "exportedElement", first: true, predicate: AzavistaExportedElementWrapperComponent, descendants: true }], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaExportedElementExporterDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[azavistaExportedElementExporter]'
                }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }]; }, propDecorators: { exportedElement: [{
                type: ViewChild,
                args: [AzavistaExportedElementWrapperComponent]
            }] } });

class AzavistaExportedElementInputDirective extends AzavistaExportedElementBase {
    get exportedElement() {
        return this.getExportedElement();
    }
    /**
     * @important Please use null coalescing operator (` ?? null`) to prevent `ExpressionChangedAfterItHasBeenCheckedError` if necessary
     *
     * @example
     *  ```
     * <some-element [azavistaExportedElementInput]="theInput?.exportedElement ?? null">
     * ```
     */
    set exportedElement(value) {
        this.setExportedElement(value);
    }
    constructor(element, host) {
        super();
        this.element = element;
        this.host = host;
    }
    setExportedElement(value) {
        super.setExportedElement(value);
        Object.assign(this.host, pick(this, ['exportedElementChanges$', 'exportedElement',]));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaExportedElementInputDirective, deps: [{ token: i0.ElementRef }, { token: i1$1.MatTab }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.10", type: AzavistaExportedElementInputDirective, selector: "[azavistaExportedElementInput]", inputs: { exportedElement: ["azavistaExportedElementInput", "exportedElement"] }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaExportedElementInputDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[azavistaExportedElementInput]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i1$1.MatTab }]; }, propDecorators: { exportedElement: [{
                type: Input,
                args: ['azavistaExportedElementInput']
            }] } });

class AzavistaExportedElementModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaExportedElementModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.10", ngImport: i0, type: AzavistaExportedElementModule, declarations: [AzavistaExportedElementWrapperComponent,
            AzavistaExportedElementExporterDirective,
            AzavistaExportedElementInputDirective,
            AzavistaMatTabGroupUseExportedElementDirective,
            AzavistaExportedElementInputDirective], imports: [CommonModule,
            AzavistaSharedModule], exports: [AzavistaExportedElementWrapperComponent,
            AzavistaExportedElementExporterDirective,
            AzavistaExportedElementInputDirective,
            AzavistaMatTabGroupUseExportedElementDirective,
            AzavistaExportedElementInputDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaExportedElementModule, imports: [CommonModule,
            AzavistaSharedModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.10", ngImport: i0, type: AzavistaExportedElementModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        AzavistaExportedElementWrapperComponent,
                        AzavistaExportedElementExporterDirective,
                        AzavistaExportedElementInputDirective,
                        AzavistaMatTabGroupUseExportedElementDirective,
                        AzavistaExportedElementInputDirective,
                    ],
                    imports: [
                        CommonModule,
                        AzavistaSharedModule,
                    ],
                    exports: [
                        AzavistaExportedElementWrapperComponent,
                        AzavistaExportedElementExporterDirective,
                        AzavistaExportedElementInputDirective,
                        AzavistaMatTabGroupUseExportedElementDirective,
                        AzavistaExportedElementInputDirective,
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AzavistaExportedElementExporterDirective, AzavistaExportedElementInputDirective, AzavistaExportedElementModule, AzavistaExportedElementWrapperComponent, AzavistaMatTabGroupUseExportedElementDirective, EXPORTED_ELEMENT_CONSUMER_CLASS_NAME, EXPORTED_ELEMENT_WRAPPER_CLASS_NAME };
